/**
 * ============================================================
 * IDENTIDADE DIGITAL DO AMBULANTE
 * ============================================================
 *
 * FONTE UNICA DE VERDADE:
 * O campo `Vendor.id` representa a IDENTIDADE DIGITAL OFICIAL
 * do ambulante no sistema E-Ambulante.
 *
 * USOS INSTITUCIONAIS:
 * - Fiscalizacao da Prefeitura (AGEFIS)
 * - QR Code oficial para verificacao em campo
 * - Referencia unica em pedidos (Order.vendorId)
 * - Comunicacao institucional (SystemMessage.vendorId)
 *
 * IMPORTANTE:
 * - NAO usar IDs alternativos (localStorage, random, etc.)
 * - NAO expor IDs internos para clientes
 * - SEMPRE usar este modulo para operacoes institucionais
 *
 * @author E-Ambulante Team
 * @since 2026-02-10
 */

import type { Vendor, VendorStatus, RegionalId } from '../types';

// ============================================================
// CONFIGURACAO DO SISTEMA
// ============================================================

/**
 * Base URL oficial do sistema E-Ambulante
 * Usado para gerar URLs institucionais (QR Codes, links oficiais)
 */
export const E_AMBULANTE_BASE_URL = 'https://e-ambulante.app';

/**
 * Prefixo institucional para o ID Digital
 * Formato: PMF-{REGIONAL}-{ID}
 * PMF = Prefeitura Municipal de Fortaleza
 */
export const INSTITUTIONAL_PREFIX = 'PMF';

// ============================================================
// FUNCOES DE IDENTIDADE DIGITAL
// ============================================================

/**
 * Retorna o ID Digital do Ambulante normalizado para uso institucional.
 *
 * Este ID e a REFERENCIA OFICIAL para:
 * - Fiscalizacao da Prefeitura
 * - QR Code institucional
 * - Identificacao em relatorios
 *
 * @param vendorId - O ID original do vendor (Vendor.id)
 * @returns O ID institucional formatado
 *
 * @example
 * getVendorDigitalId('vendor_acai_001')
 * // Retorna: 'vendor_acai_001' (normalizado)
 */
export function getVendorDigitalId(vendorId: string): string {
  if (!vendorId || typeof vendorId !== 'string') {
    throw new Error('ID Digital invalido: vendorId e obrigatorio');
  }

  // Normaliza: remove espacos, trim para consistencia
  return vendorId.trim();
}

/**
 * Formata o ID Digital para exibicao institucional.
 * Adiciona prefixo PMF e regional quando disponivel.
 *
 * @param vendor - O objeto Vendor completo
 * @returns ID formatado para exibicao oficial
 *
 * @example
 * formatVendorInstitutionalId(vendor)
 * // Retorna: 'PMF-R1-vendor_acai_001' (se regional 1)
 */
export function formatVendorInstitutionalId(vendor: Vendor): string {
  const baseId = getVendorDigitalId(vendor.id);

  if (vendor.regionalId) {
    const regionalNumber = vendor.regionalId.replace('Regional ', 'R');
    return `${INSTITUTIONAL_PREFIX}-${regionalNumber}-${baseId}`;
  }

  return `${INSTITUTIONAL_PREFIX}-${baseId}`;
}

/**
 * Gera a URL oficial do QR Code institucional.
 * Esta URL e usada EXCLUSIVAMENTE para fiscalizacao.
 *
 * @param vendorId - O ID do vendor
 * @returns URL oficial para o perfil publico institucional
 *
 * @example
 * getInstitutionalQRCodeUrl('vendor_acai_001')
 * // Retorna: 'https://e-ambulante.app/vendor/vendor_acai_001'
 */
export function getInstitutionalQRCodeUrl(vendorId: string): string {
  const digitalId = getVendorDigitalId(vendorId);
  // Mesmo host do app (ex.: producao web.app) para o fiscal escanear e o parseQRCodeUrl reconhecer
  let base = E_AMBULANTE_BASE_URL;
  if (typeof window !== 'undefined' && window.location?.origin && /^https?:\/\//i.test(window.location.origin)) {
    base = window.location.origin;
  }
  return `${base}/vendor/${digitalId}`;
}

/**
 * Extrai o vendorId de uma URL institucional do QR Code.
 *
 * @param url - URL escaneada do QR Code
 * @returns O vendorId extraido ou null se invalido
 *
 * @example
 * parseQRCodeUrl('https://e-ambulante.app/vendor/vendor_acai_001')
 * // Retorna: 'vendor_acai_001'
 */
export function parseQRCodeUrl(url: string): string | null {
  if (!url) return null;

  // Padroes aceitos:
  // https://e-ambulante.app/vendor/{id}
  // https://e-ambulante-producao.web.app/vendor/{id}
  const patterns = [
    /e-ambulante\.app\/vendor\/([^\/\?\#]+)/i,
    /e-ambulante-producao\.web\.app\/vendor\/([^\/\?\#]+)/i,
    /localhost.*\/vendor\/([^\/\?\#]+)/i, // Dev
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return getVendorDigitalId(match[1]);
    }
  }

  // Fallback: se for apenas o ID direto
  if (!url.includes('/') && !url.includes('http')) {
    return getVendorDigitalId(url);
  }

  return null;
}

// ============================================================
// FUNCOES DE STATUS LEGAL
// ============================================================

/**
 * Retorna o texto oficial do status legal do ambulante.
 * Nomenclatura alinhada com a Prefeitura de Fortaleza.
 *
 * IMPORTANTE:
 * - REGULATED: Mostra apenas a regional abreviada (R1, R2, etc.)
 * - UNREGULATED: Nao mostra nada (empreendedores com CNPJ/MEI, trabalham em casa)
 * - Outros status: Mostram informacao relevante para fiscalizacao
 *
 * @param status - Status do vendor
 * @param regionalId - Regional para vendors REGULATED (opcional)
 */
export function getStatusLegalText(status: VendorStatus, regionalId?: string): string {
  // REGULATED: Mostra apenas a regional abreviada
  if (status === 'REGULATED' && regionalId) {
    const regionalNumber = regionalId.replace('Regional ', 'R');
    return regionalNumber;
  }

  if (status === 'REGULATED') {
    return 'Cadastrado';
  }

  // UNREGULATED: Nao mostrar nada (empreendedores autonomos)
  if (status === 'UNREGULATED') {
    return ''; // Vazio - nao discriminar
  }

  const statusTexts: Record<string, string> = {
    'PENDING_VERIFICATION': 'Aguardando Verificacao',
    'SUSPENDED': 'Licenca Suspensa',
    'REVOKED': 'Licenca Revogada',
  };

  return statusTexts[status] || '';
}

/**
 * Retorna a cor do badge de status legal.
 * UNREGULATED usa cores neutras (nao-discriminatorias).
 */
export function getStatusLegalColor(status: VendorStatus): {
  bg: string;
  text: string;
  border: string;
  dot?: string;
} {
  const colors: Record<VendorStatus, { bg: string; text: string; border: string; dot?: string }> = {
    'REGULATED': {
      bg: 'bg-emerald-100',
      text: 'text-emerald-800',
      border: 'border-emerald-500',
      dot: 'bg-emerald-500',
    },
    'PENDING_VERIFICATION': {
      bg: 'bg-amber-100',
      text: 'text-amber-800',
      border: 'border-amber-500',
      dot: 'bg-amber-500',
    },
    'UNREGULATED': {
      bg: 'bg-slate-50',
      text: 'text-slate-600',
      border: 'border-slate-200',
      dot: 'bg-slate-400',
    },
    'SUSPENDED': {
      bg: 'bg-orange-100',
      text: 'text-orange-800',
      border: 'border-orange-500',
      dot: 'bg-orange-500',
    },
    'REVOKED': {
      bg: 'bg-red-100',
      text: 'text-red-800',
      border: 'border-red-500',
      dot: 'bg-red-500',
    },
  };

  return colors[status] || colors['UNREGULATED'];
}

/**
 * Retorna o icone recomendado para cada status (nome Lucide).
 * UNREGULATED nao tem icone visivel.
 */
export function getStatusLegalIcon(status: VendorStatus): string {
  const icons: Record<VendorStatus, string> = {
    'REGULATED': 'shield-check',
    'PENDING_VERIFICATION': 'clock',
    'UNREGULATED': '',
    'SUSPENDED': 'alert-triangle',
    'REVOKED': 'x-circle',
  };

  return icons[status] || '';
}

// ============================================================
// VALIDACOES INSTITUCIONAIS
// ============================================================

const MAX_REGIONAL_NUM = 12;

function regionalNumberToCanonical(n: number): string {
  if (!Number.isFinite(n) || n < 1 || n > MAX_REGIONAL_NUM) return '';
  return `Regional ${n}`;
}

/**
 * Normaliza texto de regional para o formato canonico "Regional 1" … "Regional 12".
 * Equivalencias tratadas (ex.): "Regional 2", "regional-2", "regional_2", "2", "r2", "R2".
 * Fora do intervalo 1–12 retorna string vazia (sem adivinhar regional).
 */
export function normalizeRegionalId(id: string | undefined | null): string {
  if (id == null || id === '') return '';

  const raw = String(id).trim();
  if (!raw) return '';

  // Ja no formato canonico
  const canonicalMatch = raw.match(/^Regional\s+(1[0-2]|[1-9])$/i);
  if (canonicalMatch) {
    return regionalNumberToCanonical(parseInt(canonicalMatch[1], 10));
  }

  // Remove espacos e separadores comuns; mantem letras para padroes r2 / regional2
  const compact = raw
    .replace(/[\s\u00A0]+/g, '')
    .replace(/[-_]/g, '')
    .toLowerCase();

  const rOnly = compact.match(/^r(1[0-2]|[1-9])$/);
  if (rOnly) {
    return regionalNumberToCanonical(parseInt(rOnly[1], 10));
  }

  const regionalPrefix = compact.match(/^regional(1[0-2]|[1-9])$/);
  if (regionalPrefix) {
    return regionalNumberToCanonical(parseInt(regionalPrefix[1], 10));
  }

  if (/^(1[0-2]|[1-9])$/.test(compact)) {
    return regionalNumberToCanonical(parseInt(compact, 10));
  }

  const digit = compact.match(/(1[0-2]|[1-9])/);
  if (digit) {
    return regionalNumberToCanonical(parseInt(digit[1], 10));
  }

  return '';
}

/** Compara duas regionais usando normalizacao canonica (1–12). */
export function regionalIdsEqual(
  a: string | undefined | null,
  b: string | undefined | null
): boolean {
  const na = normalizeRegionalId(a);
  const nb = normalizeRegionalId(b);
  if (!na || !nb) return false;
  return na === nb;
}

/**
 * Valida se um vendor pode ser fiscalizado por um fiscal de determinada regional.
 *
 * @param vendor - O vendor a ser fiscalizado
 * @param fiscalRegionalId - A regional do fiscal
 * @param options - Opcoes adicionais (viewAllRegionals, additionalRegionals)
 * @returns true se o fiscal pode fiscalizar este vendor
 */
export function canFiscalizeVendor(
  vendor: Vendor,
  fiscalRegionalId: RegionalId,
  options?: { viewAllRegionals?: boolean; additionalRegionals?: RegionalId[] }
): boolean {
  if (options?.viewAllRegionals) return true;
  if (!vendor.regionalId) return true;
  const vendorNorm = normalizeRegionalId(vendor.regionalId);
  if (vendorNorm === normalizeRegionalId(fiscalRegionalId)) return true;
  if (options?.additionalRegionals?.length) {
    return options.additionalRegionals.some(
      r => normalizeRegionalId(r) === vendorNorm
    );
  }
  return false;
}

/**
 * Valida se o vendor tem todos os dados obrigatorios para regularizacao.
 */
export function hasRequiredDocuments(vendor: Vendor): {
  isComplete: boolean;
  missing: string[];
} {
  const missing: string[] = [];

  if (!vendor.name) missing.push('Nome');
  if (!vendor.cpfCnpj) missing.push('CPF/CNPJ');
  if (!vendor.phone) missing.push('Telefone');
  if (!vendor.image) missing.push('Foto');
  if (!vendor.location?.lat || !vendor.location?.lng) missing.push('Localizacao');

  return {
    isComplete: missing.length === 0,
    missing,
  };
}

// ============================================================
// TIPOS EXPORTADOS
// ============================================================

export interface VendorPublicProfile {
  /** Identidade Digital do Ambulante */
  digitalId: string;
  /** ID Institucional formatado (PMF-R1-xxx) */
  institutionalId: string;
  /** Nome do ambulante/estabelecimento */
  name: string;
  /** Nome da loja/ponto */
  storeName: string;
  /** Foto do ambulante/ponto (se disponivel) */
  image?: string;
  /** Status legal perante a Prefeitura */
  vendorStatus: VendorStatus;
  /** Texto oficial do status */
  statusText: string;
  /** Regional de atuacao */
  regionalId?: RegionalId;
  /** Numero da licenca/autorizacao */
  permitNumber?: string;
  /** Data de regularizacao */
  regulatedAt?: string;
  /** Fiscal que aprovou */
  approvedBy?: string;
  /** URL do QR Code institucional */
  qrCodeUrl: string;
}

/**
 * Converte um Vendor completo para o perfil publico institucional.
 * Remove dados sensiveis (email, telefone, etc.)
 */
export function toVendorPublicProfile(vendor: Vendor): VendorPublicProfile {
  return {
    digitalId: getVendorDigitalId(vendor.id),
    institutionalId: formatVendorInstitutionalId(vendor),
    name: vendor.name,
    storeName: vendor.storeName,
    image: vendor.image,
    vendorStatus: vendor.vendorStatus,
    statusText: getStatusLegalText(vendor.vendorStatus, vendor.regionalId),
    regionalId: vendor.regionalId,
    permitNumber: vendor.permitNumber,
    regulatedAt: vendor.regulatedAt,
    approvedBy: vendor.approvedBy,
    qrCodeUrl: getInstitutionalQRCodeUrl(vendor.id),
  };
}
